/**
 * 富文本编辑器配置工具
 * 同时支持 UEditor 和 wangEditor 的配置
 */

// ============================================================
// UEditor 配置
// ============================================================

// UEditor 工具栏配置（对应 toolbars）
const ueditorDefaultToolbarConfig = [
    ['fullscreen', 'source', '|', 'undo', 'redo', '|',
        'bold', 'italic', 'underline', 'fontborder', 'strikethrough', 'superscript', 'subscript', 'removeformat', 'formatmatch', 'autotypeset', 'blockquote', 'pasteplain', '|',
        'forecolor', 'backcolor', 'insertorderedlist', 'insertunorderedlist', 'selectall', 'cleardoc', '|',
        'rowspacingtop', 'rowspacingbottom', 'lineheight', '|',
        'customstyle', 'paragraph', 'fontfamily', 'fontsize', '|',
        'directionalityltr', 'directionalityrtl', 'indent', '|',
        'justifyleft', 'justifycenter', 'justifyright', 'justifyjustify', '|', 'touppercase', 'tolowercase', '|',
        // 'link', 'unlink', '|', 'imagenone', 'imageleft', 'imageright', 'imagecenter', '|',
        // 'insertimage', 'emotion', 'scrawl', 'insertvideo', 'attachment', 'map', 'gmap', 'insertframe', 'insertcode', 'webapp', 'pagebreak', 'template', 'background', '|',
        // 'horizontal', 'date', 'time', 'spechars', 'snapscreen', 'wordimage', '|',
        // 'inserttable', 'deletetable', 'insertparagraphbeforetable', 'insertrow', 'deleterow', 'insertcol', 'deletecol', 'mergecells', 'mergeright', 'mergedown', 'splittocells', 'splittorows', 'splittocols', 'charts', '|',
        // 'print', 'preview', 'searchreplace', 'help', 'drafts'
    ]
]

const ueditorSimpleToolbarConfig = [
    ['bold', 'italic', 'underline', '|',
        'forecolor', 'backcolor', '|',
        'fontfamily', 'fontsize', '|',
        'justifyleft', 'justifycenter', 'justifyright', '|',
        'insertorderedlist', 'insertunorderedlist', '|',
        'link', 'insertimage', '|',
        'undo', 'redo', '|',
        'source', 'fullscreen']
]

const ueditorBasicToolbarConfig = [
    ['bold', 'italic', 'underline', '|',
        'insertorderedlist', 'insertunorderedlist', '|',
        'fontfamily', 'fontsize', '|',
        'justifyleft', 'justifycenter', 'justifyright', '|',
        'undo', 'redo']
]

/**
 * 创建 UEditor 配置
 * @param {Object} options 配置选项
 * @returns {Object} UEditor 配置
 */
export function createUEConfig(options = {}) {
    const {
        placeholder = '请输入内容...',
        readonly = false,
        initialFrameWidth = null,
        initialFrameHeight = 300,
        toolbars = ueditorDefaultToolbarConfig,
        serverUrl = '',
        zIndex = 900,
        ...rest
    } = options

    return {
        serverUrl: serverUrl,
        initialFrameWidth: initialFrameWidth,
        initialFrameHeight: initialFrameHeight,
        readonly: readonly,
        placeholder: placeholder,
        toolbars: toolbars,
        zIndex: zIndex,
        autoClearinitialContent: false,
        enableAutoSave: false,
        saveInterval: 500,
        wordCount: false,
        elementPathEnabled: false,
        enableContextMenu: true,
        autoSyncData: false,
        pasteplain: false,
        filterTxtRules: function () {
            return {
                '-': 'script style object iframe embed input select',
                'p': { '$': {} },
                'br': { '$': {} },
                'div': { '$': {} },
                'li': { '$': {} },
                'caption': function (node) { node.tagName = 'p' },
                'th': function (node) { node.tagName = 'p' },
                'tr': function (node) { node.tagName = 'p' },
                'h1': { '$': {} }, 'h2': { '$': {} }, 'h3': { '$': {} },
                'h4': { '$': {} }, 'h5': { '$': {} }, 'h6': { '$': {} },
                'td': function (node) {
                    var txt = !!node.innerText()
                    if (txt) {
                        node.parentNode.insertAfter(
                            window.UE.uNode.createText(' &nbsp; &nbsp;'),
                            node
                        )
                    }
                    node.parentNode.removeChild(node, node.innerText())
                }
            }
        }(),
        ...rest
    }
}

/**
 * 根据 toolbarType 返回对应的工具栏配置
 * @param {string} type 类型：'default' | 'simple' | 'basic'
 * @returns {Array} UEditor toolbars 配置
 */
export function createToolbarConfig(type = 'default') {
    switch (type) {
        case 'simple': return ueditorSimpleToolbarConfig
        case 'basic': return ueditorBasicToolbarConfig
        default: return ueditorDefaultToolbarConfig
    }
}

// ============================================================
// wangEditor 配置（保留以兼容现有代码）
// ============================================================

export const defaultToolbarConfig = {
    toolbarKeys: [
        'headerSelect',
        'blockquote',
        '|',
        'bold',
        'underline',
        'italic',
        {
            key: 'group-more-style',
            title: '更多样式',
            iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M204.8 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M505.6 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path><path d="M806.4 505.6m-76.8 0a76.8 76.8 0 1 0 153.6 0 76.8 76.8 0 1 0-153.6 0Z"></path></svg>',
            menuKeys: ['through', 'code', 'sup', 'sub', 'clearStyle']
        },
        'color',
        'bgColor',
        '|',
        'fontSize',
        'fontFamily',
        'lineHeight',
        '|',
        'bulletedList',
        'numberedList',
        'todo',
        {
            key: 'group-justify',
            title: '对齐',
            iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M768 793.6v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z m-204.8-230.4v102.4H51.2v-102.4h716.8z m204.8-230.4v102.4H51.2v-102.4h921.6z"></path></svg>',
            menuKeys: ['justifyLeft', 'justifyCenter', 'justifyRight', 'justifyJustify']
        },
        {
            key: 'group-indent',
            title: '缩进',
            iconSvg: '<svg viewBox="0 0 1024 1024"><path d="M0 64h1024v128H0z m384 192h640v128H384z m0 192h640v128H384z m0 192h640v128H384zM0 832h1024v128H0z m0-128V320l256 192z"></path></svg>',
            menuKeys: ['indent', 'delIndent']
        },
        'insertTable',
        'divider',
        '|',
        'undo',
        'redo',
        '|',
        'fullScreen'
    ]
}

export const simpleToolbarConfig = {
    toolbarKeys: [
        'bold',
        'underline',
        'italic',
        '|',
        'color',
        'bgColor',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'insertLink',
        'insertImage',
        '|',
        'undo',
        'redo'
    ]
}

export const basicToolbarConfig = {
    toolbarKeys: [
        'bold',
        'underline',
        'italic',
        '|',
        'bulletedList',
        'numberedList',
        '|',
        'undo',
        'redo'
    ]
}

export function createEditorConfig(options = {}) {
    const {
        placeholder = '请输入内容...',
        uploadImageUrl = '/api/upload-image',
        uploadVideoUrl = '/api/upload-video',
        maxFileSize = 5,
        allowedImageTypes = ['image/*'],
        allowedVideoTypes = ['video/*'],
        headers = {},
        meta = {},
        ...rest
    } = options

    return {
        ...rest,
        placeholder,
        MENU_CONF: {
            uploadImage: {
                server: uploadImageUrl,
                fieldName: 'file',
                maxFileSize: maxFileSize * 1024 * 1024,
                allowedFileTypes: allowedImageTypes,
                headers,
                meta,
                metaWithUrl: false,
                withCredentials: false,
                timeout: 15 * 1000,
                onBeforeUpload(file) {
                    console.log('onBeforeUpload', file)
                    return file
                },
                onProgress(progress) {
                    console.log('onProgress', progress)
                },
                onSuccess(file, res) {
                    console.log('onSuccess', file, res)
                },
                onFailed(file, res) {
                    console.log('onFailed', file, res)
                },
                onError(file, err, res) {
                    console.log('onError', file, err, res)
                }
            },
            uploadVideo: {
                server: uploadVideoUrl,
                fieldName: 'file',
                maxFileSize: maxFileSize * 10 * 1024 * 1024,
                allowedFileTypes: allowedVideoTypes,
                headers,
                meta,
                metaWithUrl: false,
                withCredentials: false,
                timeout: 60 * 1000,
                onBeforeUpload(file) {
                    console.log('onBeforeUpload', file)
                    return file
                },
                onProgress(progress) {
                    console.log('onProgress', progress)
                },
                onSuccess(file, res) {
                    console.log('onSuccess', file, res)
                },
                onFailed(file, res) {
                    console.log('onFailed', file, res)
                },
                onError(file, err, res) {
                    console.log('onError', file, err, res)
                }
            }
        }
    }
}

export function getDefaultEditorConfig(placeholder = '请输入内容...') {
    return createEditorConfig({ placeholder })
}

export function getDefaultToolbarConfig() {
    return defaultToolbarConfig
}
